import {
  CONFIG_ENVIRONMENT,
  CONFIG_VERSION
} from '../../../config'
import { getCacheBuster } from '../../../util/cache-buster'
import { ContentInfo, UppClientOptions } from '../../interfaces'
import { getBrandDomain } from '../../../util/brand-domain'
import { debug, error } from '@mamdev/navigator.frontend-logger'
import { getProfileParams } from './profile-params'
import { getUppTargetingData } from '../../../targeting-cookie'

export const ADITION_CONFIG_DEFAULT = {
  TAGID: 'permission',
  LAYOUTCLASS: 'b',
  REQUEST_TYPE: 'x',
  NETWORK: '42',
  ACCMOD: '1',
}

const smAdiDomainMap: { [key: string]: string } = {
  gmx: 'gmx.net',
  gmxat: 'gmx.net',
  gmxch: 'gmx.net',
  gmxcouk: 'gmx.com',
  gmxes: 'gmx.com',
  gmxfr: 'gmx.com',
  gmxcom: 'gmx.com',
  mailcom: 'mail.com',
  webde: 'web.de',
}

const smAdiSubDomainMap: { [key: string]: string } = {
  qa: 'smadi-qa',
  prelive: 'smadi',
  live: 'smadi',
}

const smAdiEndpoint = 'uppcontent'

/**
 * Get the URL for the SmAdi request
 * @param clientConfig
 * @param params Additional request parameters
 * @param wi window ID used for Adition frequency capping
 */

export const getSmAdiUrl = (clientConfig: UppClientOptions,
                            params: { [key: string]: string } = {},
                            wi = getCacheBuster()): string => {
  let stage = 'live';
  /* istanbul ignore next else */
  if (clientConfig.stage) {
    stage = clientConfig.stage;
  } else if (String(CONFIG_ENVIRONMENT) === 'QA') {
    stage = 'qa';
  }

  const smAdiParams: Record<string, string> = {
    ...getAditionBaseParams(clientConfig),
    ...params,
    wi,
  }

  if (clientConfig.section?.startsWith('app_and') || clientConfig.section?.startsWith('app_ios')) {
    smAdiParams.settpidcookie = 'true'
  }

  const searchParams = new URLSearchParams(smAdiParams)
  let baseUrl: string
  if (typeof clientConfig.smAdiUrl === 'string') {
    baseUrl = clientConfig.smAdiUrl
  } else {
    baseUrl = `https://${smAdiSubDomainMap[stage]}.${smAdiDomainMap[clientConfig.portal]}/${smAdiEndpoint}`
  }

  return `${baseUrl}?${searchParams}`
}

/**
 * Get the content data from SmAdi's JSON response
 * @param clientConfig
 * @param params additional request parameters
 * @param wi window ID used for Adition frequency capping
 */
export const getContentInfo = async (clientConfig: UppClientOptions,
                               params?: { [key: string]: string},
                               wi = getCacheBuster()): Promise<ContentInfo> => {
  const fetchInit: RequestInit = {
    method: 'GET',
  }

  const headers = new Headers()
  headers.append('Authorization', `Bearer ${clientConfig.accessToken}`)
  headers.append('Accept', 'application/json')
  headers.append('Accept-Charset', 'utf-8')
  headers.append('X-UI-APP', `${clientConfig.xUIApp}`)
  fetchInit.headers = headers

  const url = getSmAdiUrl(clientConfig, params, wi).toString()
  debug('Sending SmAdi request', url)
  const res = await fetch(url, fetchInit)

  if (res.status >= 400) {
    throw new Error(`SmAdi request failed with status ${res.status} ${res.statusText}`)
  }

  const contentType = res.headers.get('content-type').toLowerCase()
  if (!contentType.includes('application/json')) {
    throw new Error(`SmAdi response has unexpected content-type ${contentType}`)
  }

  let json
  try {
    json = await res.json()
  } catch (e) {
    const text = await res.text()
    throw new Error('Failed to parse SmAdi response as JSON. Response text: ' + text?.slice(0, 64))
  }

  if (!Object.prototype.hasOwnProperty.call(json, 'content_url')) {
    throw new Error(`Unexpected SmAdi response body ${JSON.stringify(json)}`)
  }

  return json as ContentInfo
}

export const getAditionBaseParams = (clientConfig: UppClientOptions): { [key: string]: string } => {
  let stage = 'live';
  /* istanbul ignore next else */
  if (clientConfig.stage) {
    stage = clientConfig.stage;
  } else if (String(CONFIG_ENVIRONMENT) === 'QA') {
    stage = 'qa';
  }
  const section = clientConfig.section || clientConfig.category
  const tagid = clientConfig.tagid || ADITION_CONFIG_DEFAULT.TAGID
  const layoutclass = clientConfig.layoutclass || ADITION_CONFIG_DEFAULT.LAYOUTCLASS

  let campaign = clientConfig.campaign instanceof Array ? clientConfig.campaign.join(',') : clientConfig.campaign
  if (campaign) {
    campaign = `,${campaign},`
  }

  let targetingData: Record<string, string> = {}
  try {
    targetingData = getUppTargetingData()
  } catch (e) {
    error('Error getting targeting data', e)
  }

  return {
    ...targetingData,
    wpt: ADITION_CONFIG_DEFAULT.REQUEST_TYPE,
    nw: ADITION_CONFIG_DEFAULT.NETWORK,
    accmod: ADITION_CONFIG_DEFAULT.ACCMOD,
    lt: `portal(${clientConfig.portal})category(${clientConfig.category})section(${section})tagid(${tagid})layoutclass(${layoutclass})`,
    ref: (document.referrer ? encodeURIComponent(new URL(document.referrer).origin) : ''),
    ...getProfileParams({
      portal: clientConfig.portal,
      stage,
      category: clientConfig.category,
      section,
      tagid,
      layoutclass,
      upp: '1',
      portal_url: encodeURIComponent(getBrandDomain(clientConfig.portal)),
      campaign,
      version: CONFIG_VERSION
    }),
    ...(clientConfig.xUIApp ? getProfileParams({ x_ui_app: clientConfig.xUIApp }) : {}),
    ...(clientConfig.forceContent ? getProfileParams({ force_content: clientConfig.forceContent }) : {}),
    ...(clientConfig.contentGroup ? getProfileParams({ content_group: clientConfig.contentGroup }) : {}),
    ...(clientConfig.profileParameters ? getProfileParams(clientConfig.profileParameters) : {}),
  }
}
